import React, { useState } from 'react';
import { X, Copy, Check, MessageCircle, QrCode, Download, Share2 } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    url: string;
    title: string;
    showToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, url, title, showToast }) => {
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const handleCopy = () => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        if (showToast) showToast('Link copiado!', 'success');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleWhatsApp = () => {
        const message = encodeURIComponent(`Confira a loja ${title} no ZapShop! 🚀\n\n${url}`);
        window.open(`https://wa.me/?text=${message}`, '_blank');
    };

    const downloadQRCode = () => {
        const canvas = document.getElementById('share-qr-code') as HTMLCanvasElement;
        if (canvas) {
            const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
            let downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            downloadLink.download = `qrcode-${title.toLowerCase().replace(/\s+/g, '-')}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl relative animate-slide-up border border-gray-100 overflow-hidden">
                {/* Decoração superior */}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-brand-500" />

                <button onClick={onClose} className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors">
                    <X size={20} />
                </button>

                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-brand-100">
                        <Share2 size={32} className="text-brand-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Compartilhar Loja</h3>
                    <p className="text-sm text-gray-500 mt-1">Divulgue sua vitrine para o mundo</p>
                </div>

                <div className="space-y-3 mb-8">
                    <button
                        onClick={handleWhatsApp}
                        className="w-full flex items-center gap-4 p-3.5 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#075E54] rounded-2xl transition-all font-bold group border border-[#25D366]/20"
                    >
                        <div className="w-10 h-10 bg-[#25D366] text-white rounded-xl flex items-center justify-center shadow-lg group-hover:rotate-6 transition-transform">
                            <MessageCircle size={20} fill="currentColor" />
                        </div>
                        WhatsApp
                    </button>

                    <button
                        onClick={handleCopy}
                        className="w-full flex items-center gap-4 p-3.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-2xl transition-all font-bold group border border-gray-200"
                    >
                        <div className="w-10 h-10 bg-gray-900 text-white rounded-xl flex items-center justify-center shadow-lg group-hover:-rotate-6 transition-transform">
                            {copied ? <Check size={20} /> : <Copy size={20} />}
                        </div>
                        {copied ? 'Copiado!' : 'Copiar Link'}
                    </button>
                </div>

                <div className="bg-gray-50 rounded-[2.5rem] p-6 border border-gray-100 text-center relative overflow-hidden">
                    <div className="flex justify-center mb-4 bg-white p-5 rounded-3xl inline-block mx-auto shadow-sm border border-gray-100 relative z-10">
                        <QRCodeCanvas
                            id="share-qr-code"
                            value={url}
                            size={140}
                            level="H"
                            includeMargin={false}
                            imageSettings={{
                                src: "/logozapshopsaas.png",
                                x: undefined, y: undefined, height: 28, width: 28, excavate: true,
                            }}
                        />
                    </div>

                    <button
                        onClick={downloadQRCode}
                        className="w-full flex items-center justify-center gap-2 py-2 text-sm font-bold text-brand-600 hover:text-brand-700 transition relative z-10"
                    >
                        <Download size={16} />
                        Baixar QR Code
                    </button>

                    {/* Marca d'água decorativa */}
                    <QrCode size={120} className="absolute -bottom-6 -right-6 text-gray-200/50 -rotate-12 pointer-events-none" />
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
